import { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import type { DefaultTheme } from 'styled-components';

interface PlotlyChartRendererProps {
  chart: string;
  className?: string;
}

// Plotly `table` traces carry their own header/cell styling. Re-skin them to
// match the app theme so embedded tables don't render with Chart Studio colors.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyThemeToTraces(traces: any[], theme: DefaultTheme) {
  return traces.map((trace) => {
    if (trace.type !== 'table') return trace;

    const themed = { ...trace };
    const header = { ...(themed.header ?? {}) };
    const cells = { ...(themed.cells ?? {}) };

    header.fill = { ...header.fill, color: theme.colors.zinc800 };
    header.font = { ...header.font, color: theme.colors.textPrimary, size: 13 };
    header.line = {
      ...header.line,
      color: theme.colors.sectionBorder,
      width: 1,
    };
    header.align = header.align ?? 'center';

    if (!cells.fill?.color) {
      cells.fill = { ...cells.fill, color: theme.colors.bgPage2 };
    }
    cells.font = { ...cells.font, color: theme.colors.textRegular, size: 13 };
    cells.line = { ...cells.line, color: theme.colors.sectionBorder, width: 1 };

    themed.header = header;
    themed.cells = cells;
    return themed;
  });
}

// Strip any Chart Studio styling baked into the figure and re-apply the app
// theme (colors, fonts, axis/grid styling) so every chart looks native.
function buildThemedLayout(
  layout: Record<string, unknown>,
  theme: DefaultTheme
): Record<string, unknown> {
  const clean = { ...layout };
  delete clean.template;
  delete clean.paper_bgcolor;
  delete clean.plot_bgcolor;
  delete clean.font;

  const colorway = [
    theme.colors.primary,
    theme.colors.secondary,
    theme.colors.textDanger,
    theme.colors.highlight,
    theme.colors.textSuccess,
    theme.colors.textRegular,
  ];

  const axisDefaults = {
    gridcolor: theme.colors.navigationDivider,
    automargin: true,
    zerolinecolor: theme.colors.sectionBorder,
    color: theme.colors.textRegular,
    tickfont: { size: 13 },
    title: { font: { size: 13 } },
  };

  return {
    ...clean,
    paper_bgcolor: theme.colors.bgPage2,
    plot_bgcolor: theme.colors.bgPage2,
    font: { color: theme.colors.textPrimary, size: 13 },
    template: {
      layout: {
        font: { color: theme.colors.textRegular, size: 13 },
        title: { font: { color: theme.colors.textPrimary, size: 15 } },
        xaxis: axisDefaults,
        yaxis: axisDefaults,
        colorway,
        plot_bgcolor: theme.colors.bgPage2,
        paper_bgcolor: theme.colors.bgPage2,
      },
    },
  };
}

export default function PlotlyChartRenderer({
  chart,
  className,
}: PlotlyChartRendererProps) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!chart || !containerRef.current) return;

    let cancelled = false;
    const node = containerRef.current;

    const renderChart = async () => {
      try {
        setError('');
        const parsed = JSON.parse(chart);
        const { data, layout = {} } = parsed;

        if (!Array.isArray(data)) {
          setError('Invalid chart data: "data" must be an array');
          return;
        }

        // Loaded lazily so plotly.js (~3MB) is only fetched on pages with charts.
        const Plotly = await import('plotly.js-dist-min');
        if (cancelled) return;

        const themedData = applyThemeToTraces(data, theme);
        const themedLayout = buildThemedLayout(layout, theme);

        await Plotly.react(node, themedData, themedLayout, {
          responsive: true,
          displayModeBar: false,
        });
      } catch (err) {
        if (!cancelled) {
          console.error('Plotly rendering error:', err);
          setError(
            `Error rendering chart: ${
              err instanceof Error ? err.message : 'Unknown error'
            }`
          );
        }
      }
    };

    renderChart();

    return () => {
      cancelled = true;
      import('plotly.js-dist-min').then((Plotly) => {
        Plotly.purge(node);
      });
    };
  }, [chart, theme]);

  if (error) {
    return (
      <div
        className={`plotly-error ${className || ''}`}
        style={{
          background: theme.colors.elementBgDanger,
          border: `1px solid ${theme.colors.textDanger}`,
          borderRadius: theme.dimensions.sectionBorderRadius,
          padding: '12px',
          color: theme.colors.textDanger,
          fontFamily: theme.fonts.playground,
        }}
      >
        <strong>Chart Error:</strong> {error}
      </div>
    );
  }

  return (
    <PlotlyCard className={className || ''}>
      <div ref={containerRef} />
    </PlotlyCard>
  );
}

const PlotlyCard = styled.div`
  margin: 12px auto;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.sectionBorder};
  border-radius: ${({ theme }) => theme.dimensions.sectionBorderRadius};
  overflow: hidden;

  .js-plotly-plot,
  .plot-container,
  .svg-container {
    width: 100%;
    border-radius: ${({ theme }) => theme.dimensions.sectionBorderRadius};
    overflow: hidden;
  }
`;
